/**
 * Payload shapes of the template views (`template_proposal`, `template_change`,
 * `wizard_preview`), duplicated from the backend's `AgentView` union
 * (built by `apps/backend/src/lib/agent/tools/templates/viewTools.ts`,
 * `templateChangePreview.ts` and `templateWizardPreview.ts`) — no shared
 * lib between backend and frontend. A view arrives as the loosely typed
 * `AgentViewSpec` (`{ component: string } & Record<string, unknown>`), so
 * every shape here has a zod schema that narrows it safely; a view that
 * fails validation is treated as absent by the component that reads it.
 */
import { z } from "zod";

export const templateNodeKindValues = [
  "section",
  "decision",
  "position",
  "product",
  "product_placeholder",
  "template",
  "template_type",
] as const;
export type TemplateNodeKind = (typeof templateNodeKindValues)[number];

/**
 * One piece of a node's display detail (task 8.6/8.7/8.8) — a discriminated
 * union rather than a pre-formatted string, so numbers/currency are formatted
 * per the user's locale and a marker kind (`unpaid`, `freeText`, ...) is
 * looked up through Tolgee here, instead of the backend baking in German.
 * Mirrors the backend's `NodeDetail` (`templateChangePreview.ts`).
 */
export const nodeDetailSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), value: z.string() }),
  z.object({
    kind: z.literal("quantity"),
    quantity: z.number(),
    unit: z.string(),
  }),
  z.object({ kind: z.literal("price"), amount: z.number() }),
  z.object({ kind: z.literal("duration"), minutes: z.number() }),
  z.object({ kind: z.literal("rate"), amount: z.number() }),
  z.object({ kind: z.literal("unpaid") }),
  z.object({
    kind: z.literal("decisionBehavior"),
    behavior: z.enum(["SELECT_ONE", "SELECT_MANY"]),
  }),
  z.object({ kind: z.literal("articleNumber"), value: z.string() }),
  z.object({
    kind: z.literal("reference"),
    refKind: z.enum(["template", "productType"]),
    label: z.string(),
  }),
  z.object({ kind: z.literal("templateTypePlaceholder") }),
  z.object({
    kind: z.literal("options"),
    values: z.array(z.union([z.string(), z.number()])),
  }),
  z.object({ kind: z.literal("freeText") }),
]);
export type NodeDetail = z.infer<typeof nodeDetailSchema>;

export type ProposalNode = {
  kind: TemplateNodeKind;
  title: string;
  /** Quantity, price, referenced type/template/product class — raw, structured, not yet formatted or translated. */
  detail: NodeDetail[] | null;
  /** App page of a referenced building block or catalogue product. */
  url?: string;
  children: ProposalNode[];
};

const proposalNodeSchema: z.ZodType<ProposalNode> = z.lazy(() =>
  z.object({
    kind: z.enum(templateNodeKindValues),
    title: z.string(),
    detail: z.array(nodeDetailSchema).nullable(),
    url: z.string().optional(),
    children: z.array(proposalNodeSchema),
  })
);

/**
 * A `questionChanges[].before`/`after` value (task 10.5): the question's
 * label plus, when its `askIf` is set (and not `"true"`), the raw condition
 * text — locale-agnostic, structured data instead of a pre-joined German
 * string. Rendering (the label, translated "only if {condition}" suffix) is
 * task 10.6's `ChangeView.tsx` concern.
 */
export type ChangeQuestionValue = { label: string; askIf?: string };

/** A question's stored default value, as `questionChanges[].value` carries it (task 22.1/22.2). */
export type ChangeQuestionDefaultValue =
  | string
  | number
  | boolean
  | string[]
  | number[];

/**
 * A question's type as `questionChanges[].kind` carries it (task 23.4/23.5):
 * the `questionKindValues` vocabulary of the backend's `templateBlocks.ts`.
 */
export const changeQuestionKindValues = [
  "select",
  "multi_select",
  "select_number",
  "multi_select_number",
  "text",
  "number",
  "bool",
] as const;

export type ChangeQuestionKind = (typeof changeQuestionKindValues)[number];

export type ChangeNode = {
  itemId: string | null;
  kind: TemplateNodeKind;
  title: string;
  detail: NodeDetail[] | null;
  status: "unchanged" | "added" | "removed" | "changed";
  /**
   * `field` is a stable, locale-agnostic key (task 10.4: `"title"`,
   * `"quantity"`, `"unitPrice"`, `"estimatedTime"`, `"workSellingRate"`),
   * translated by `useChangeFieldLabel` (`templateNodeDisplay.tsx`). `before`/
   * `after` stay plain text only for the (already locale-agnostic) title;
   * every numeric field is structured.
   */
  fieldChanges: Array<{
    field: string;
    before: string | NodeDetail;
    after: string | NodeDetail;
  }>;
  questionChanges: Array<{
    status: "added" | "removed" | "changed";
    key: string;
    label: string;
    before: ChangeQuestionValue | null;
    after: ChangeQuestionValue | null;
    /** Only present when this change actually touches the question's options (undefined = options untouched). */
    options?: { before: NodeDetail | null; after: NodeDetail | null };
    /** Only present when this change touches the question's unit. */
    unit?: { before: string | null; after: string | null };
    /** Only present when this change touches the question's default value. */
    value?: {
      before: ChangeQuestionDefaultValue | null;
      after: ChangeQuestionDefaultValue | null;
    };
    /** Only present when this change touches the question's journeys (sorted "askWhen/askWhom" pairs). */
    journeys?: { before: string[]; after: string[] };
    /** Only present when this change touches the question's type (task 23.4). */
    kind?: { before: ChangeQuestionKind | null; after: ChangeQuestionKind };
  }>;
  /** Whether anything in this node's subtree changes (unchanged subtrees collapse). */
  changedBelow: boolean;
  children: ChangeNode[];
};

const changeQuestionValueSchema = z.object({
  label: z.string(),
  askIf: z.string().optional(),
});

const changeQuestionDefaultValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
]);

const changeNodeSchema: z.ZodType<ChangeNode> = z.lazy(() =>
  z.object({
    itemId: z.string().nullable(),
    kind: z.enum(templateNodeKindValues),
    title: z.string(),
    detail: z.array(nodeDetailSchema).nullable(),
    status: z.enum(["unchanged", "added", "removed", "changed"]),
    fieldChanges: z.array(
      z.object({
        field: z.string(),
        before: z.union([z.string(), nodeDetailSchema]),
        after: z.union([z.string(), nodeDetailSchema]),
      })
    ),
    questionChanges: z.array(
      z.object({
        status: z.enum(["added", "removed", "changed"]),
        key: z.string(),
        label: z.string(),
        before: changeQuestionValueSchema.nullable(),
        after: changeQuestionValueSchema.nullable(),
        options: z
          .object({
            before: nodeDetailSchema.nullable(),
            after: nodeDetailSchema.nullable(),
          })
          .optional(),
        unit: z
          .object({
            before: z.string().nullable(),
            after: z.string().nullable(),
          })
          .optional(),
        value: z
          .object({
            before: changeQuestionDefaultValueSchema.nullable(),
            after: changeQuestionDefaultValueSchema.nullable(),
          })
          .optional(),
        journeys: z
          .object({
            before: z.array(z.string()),
            after: z.array(z.string()),
          })
          .optional(),
        kind: z
          .object({
            before: z.enum(changeQuestionKindValues).nullable(),
            after: z.enum(changeQuestionKindValues),
          })
          .optional(),
      })
    ),
    changedBelow: z.boolean(),
    children: z.array(changeNodeSchema),
  })
);

export const templateProposalViewSchema = z.object({
  component: z.literal("template_proposal"),
  title: z.string(),
  description: z.string().nullable(),
  assumptions: z.array(z.string()),
  tree: z.array(proposalNodeSchema),
  /** The exact blocks, unread here — carried so the next turn creates what was shown. */
  blocks: z.array(z.unknown()),
});
export type TemplateProposalView = z.infer<typeof templateProposalViewSchema>;

export const templateChangeViewSchema = z.object({
  component: z.literal("template_change"),
  title: z.string(),
  description: z.string().nullable(),
  templateId: z.string(),
  tree: z.array(changeNodeSchema),
  summary: z.object({
    added: z.number(),
    removed: z.number(),
    changed: z.number(),
  }),
  /** The exact operations, unread here — carried so the next turn applies what was shown. */
  changes: z.array(z.unknown()),
});
export type TemplateChangeView = z.infer<typeof templateChangeViewSchema>;

export const askWhenValues = [
  "onQuoteCreate",
  "onQuoteRefinement",
  "onQuoteRefinement2",
  "onQuoteRefinement3",
  "onEmbeddableWizard",
  "onCallAssistant",
] as const;
export type AskWhen = (typeof askWhenValues)[number];
export const askWhomValues = ["contractor", "contractee"] as const;
export type AskWhom = (typeof askWhomValues)[number];

export type WizardAnswer = {
  key: string;
  value: string | number | boolean | Array<string | number>;
};

/** A decision answer (task 8.4/8.5): the chosen option itemIds for one decision question. */
export type WizardDecision = {
  itemId: string;
  questionId: string;
  selected: string[];
};

export const wizardPreviewViewSchema = z.object({
  component: z.literal("wizard_preview"),
  title: z.string(),
  description: z.string().nullable(),
  templateId: z.string(),
  askWhen: z.enum(askWhenValues),
  askWhom: z.enum(askWhomValues),
  unpublished: z.boolean(),
  answers: z.array(
    z.object({
      key: z.string(),
      value: z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.array(z.union([z.string(), z.number()])),
      ]),
    })
  ),
  decisions: z
    .array(
      z.object({
        itemId: z.string(),
        questionId: z.string(),
        selected: z.array(z.string()),
      })
    )
    .default([]),
});
export type WizardPreviewView = z.infer<typeof wizardPreviewViewSchema>;

const wizardPreviewAnswerValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.union([z.string(), z.number()])),
]);

const wizardPreviewPropertySchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.string(),
  unit: z.string().nullable(),
  options: z.array(z.union([z.string(), z.number()])),
  value: wizardPreviewAnswerValueSchema.nullable(),
  visible: z.boolean(),
});

/** One question of a journey, as the embeddable-wizard endpoint returns it. */
const wizardPreviewQuestionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("properties"),
    itemId: z.string(),
    itemTitle: z.string(),
    props: z.array(wizardPreviewPropertySchema),
  }),
  z.object({
    type: z.literal("decision"),
    itemId: z.string(),
    itemTitle: z.string(),
    /** Task 8.4/8.5 — needed (with an option's own `itemId`) to answer via `decisions`. */
    questionId: z.string(),
    prompt: z.string(),
    behavior: z.string(),
    options: z.array(
      z.object({
        itemId: z.string(),
        title: z.string(),
        price: z.number().nullable(),
      })
    ),
  }),
  z.object({
    type: z.literal("other"),
    itemId: z.string(),
    itemTitle: z.string(),
    questionType: z.string(),
  }),
]);
export type WizardPreviewQuestion = z.infer<typeof wizardPreviewQuestionSchema>;

/**
 * The `agentViewData(component: "wizard_preview")` response's opaque `data`
 * (task MEIS-13516 P2: replaces the former `POST /api/agent/wizard-preview`
 * route's JSON body, ported unchanged otherwise) — validated here rather than
 * cast, since the GraphQL `JSON` scalar carries no compile-time shape.
 */
export const wizardPreviewResultSchema = z.object({
  templateId: z.string(),
  journey: z.string(),
  answersApplied: z.array(
    z.object({ key: z.string(), value: wizardPreviewAnswerValueSchema })
  ),
  questions: z.array(wizardPreviewQuestionSchema),
  notPublished: z.string().nullable(),
  gatesLost: z.array(z.string()),
});
export type WizardPreviewResult = z.infer<typeof wizardPreviewResultSchema>;
